import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminHomeHeroService, DEFAULT_HOME_HERO } from '../services/admin.homeHero.service'
import type { HomeHeroConfig } from '@/lib/homeHero'
import type { HomeHeroDocument } from '@/types'

export type HomeHeroFormValues = HomeHeroConfig

interface UseAdminHomeHeroResult {
  values: HomeHeroFormValues
  setValues: React.Dispatch<React.SetStateAction<HomeHeroFormValues>>
  lastUpdated: Date | null
  loading: boolean
  error: string | null
  saving: boolean
  save: () => Promise<void>
}

function stripUpdatedAt(doc: HomeHeroDocument): HomeHeroFormValues {
  const { updatedAt, ...rest } = doc
  void updatedAt
  return rest
}

export function useAdminHomeHero(): UseAdminHomeHeroResult {
  const [values, setValues] = useState<HomeHeroFormValues>(stripUpdatedAt(DEFAULT_HOME_HERO))
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    adminHomeHeroService
      .get()
      .then((doc) => {
        if (doc) {
          setValues(stripUpdatedAt(doc))
          setLastUpdated(doc.updatedAt)
        }
      })
      .catch((err: unknown) => setError(formatUserFacingError(err, 'Failed to load')))
      .finally(() => setLoading(false))
  }, [])

  const save = useCallback(async () => {
    setSaving(true)
    setError(null)
    try {
      await adminHomeHeroService.save(values)
      setLastUpdated(new Date())
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to save'))
      throw err
    } finally {
      setSaving(false)
    }
  }, [values])

  return { values, setValues, lastUpdated, loading, error, saving, save }
}
